import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Search, X, RefreshCw } from 'lucide-react';
import API_BASE_URL from '../Config/config';

/**
 * TeeGeeLeads — admin-console view of leads captured by the TSG AI Bot.
 *
 * Reads the auth-protected /api/crm/tee-gee-leads endpoint (super-admin only)
 * and shows each visitor's name/email/phone + interest/intent/score, with a
 * click-to-read full chat transcript. This lives in the ADMIN CONSOLE, separate
 * from the CRM contacts module.
 */

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + (localStorage.getItem('session_token') || ''),
});

const Badge = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    purple: 'bg-purple-50 text-purple-700',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  };
  if (!children) return <span className="text-gray-300">—</span>;
  return <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${tones[tone] || tones.gray}`}>{children}</span>;
};

const intentTone = (intent) =>
  intent === 'HIGH_PURCHASE_INTENT' ? 'red'
  : intent === 'WHATSAPP_REQUEST' || intent === 'CONTACT_REQUEST' ? 'amber'
  : intent ? 'purple' : 'gray';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const TranscriptDrawer = ({ lead, onClose }) => {
  if (!lead) return null;
  const convo = Array.isArray(lead.conversation) ? lead.conversation : [];
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md h-full bg-[#F6F7FB] shadow-2xl flex flex-col">
        <div className="shrink-0 px-5 py-4 flex items-center justify-between text-white"
             style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}>
          <div className="min-w-0">
            <p className="font-black truncate">{lead.name || 'Unknown'}</p>
            <p className="text-[12px] text-purple-100 truncate">{lead.email} · {lead.phone || 'no phone'}</p>
          </div>
          <button onClick={onClose} aria-label="Close transcript" className="w-9 h-9 rounded-lg hover:bg-white/15 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>
        <div className="shrink-0 px-5 py-3 flex flex-wrap gap-2 border-b border-gray-200 bg-white">
          <Badge tone="purple">{lead.primary_interest || 'general'}</Badge>
          <Badge tone={intentTone(lead.lead_intent)}>{lead.lead_intent || 'browsing'}</Badge>
          {lead.audience_type ? <Badge tone="green">{lead.audience_type}</Badge> : null}
          {Number.isFinite(lead.lead_score) ? <Badge tone="amber">score {lead.lead_score}</Badge> : null}
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {convo.length === 0 ? (
            <p className="text-sm text-gray-400 text-center mt-8">No transcript stored for this lead.</p>
          ) : convo.map((m, i) => {
            const isBot = m.from === 'bot';
            return (
              <div key={i} className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-2`}>
                <div className={`max-w-[80%] px-3 py-2 text-[13px] whitespace-pre-line rounded-2xl ${
                  isBot ? 'bg-white text-gray-800 border border-gray-100' : 'bg-purple-600 text-white'}`}>
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const TeeGeeLeads = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (q = '') => {
    setLoading(true); setError('');
    try {
      const url = `${API_BASE_URL}/api/crm/tee-gee-leads${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setTotal(data.total || 0);
    } catch (e) {
      setError('Could not load AI bot leads. Please try again.');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); load(search.trim()); };

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{ background: 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}>
            <MessageSquare size={20} />
          </span>
          <div>
            <h1 className="text-xl font-black text-gray-900">Tee Gee Leads</h1>
            <p className="text-sm text-gray-500">Visitors captured by the TSG AI Bot · {total} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={onSearch} className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search name, email, phone"
                   className="pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400 w-56" />
          </form>
          <button onClick={() => load(search.trim())} aria-label="Refresh"
                  className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50">
            <RefreshCw size={15} className={loading ? 'animate-spin text-purple-600' : 'text-gray-500'} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="text-center py-16 text-red-500 font-medium">{error}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No leads yet. They'll appear here as visitors chat with Tee Gee.</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-black uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Interest</th>
                <th className="px-4 py-3">Intent</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-gray-50 hover:bg-purple-50/40 cursor-pointer" onClick={() => setSelected(l)}>
                  <td className="px-4 py-3 font-bold text-gray-900">{l.name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{l.email || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{l.phone || '—'}</td>
                  <td className="px-4 py-3"><Badge tone="purple">{l.primary_interest}</Badge></td>
                  <td className="px-4 py-3"><Badge tone={intentTone(l.lead_intent)}>{l.lead_intent}</Badge></td>
                  <td className="px-4 py-3 font-bold text-gray-700">{Number.isFinite(l.lead_score) ? l.lead_score : '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(l.updated_at || l.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-[12px] font-bold text-purple-600">
                      <MessageSquare size={13} /> View
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TranscriptDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default TeeGeeLeads;
